/**
 * FOTA Web Migration Page
 * Ladda upp config-filer och skicka dem till enheter via Teltonika FOTA Web TxConfiguration-tasks.
 * Enheter hämtar config-filen nästa gång de checkar in mot FOTA Web — oavsett vilken server de pekar mot.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  CloudArrowUpIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  TrashIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Organization {
  id: number;
  name: string;
}

interface FotaFile {
  id: number;
  name: string;
  size: number;
  created_at?: string;
  type?: string;
}

type FileKind = 'config' | 'firmware' | 'log' | 'other';
type FileFilter = 'all' | 'config' | 'firmware' | 'log';

/**
 * Klassificerar en FOTA-fil. Prioriterar FOTA Webs type-fält (configuration,
 * firmware, can_chip_firmware, fw_bundle, log) med filändelse som fallback.
 */
const getFileKind = (f: { name?: string; type?: string }): FileKind => {
  const type = (f.type || '').toLowerCase();
  if (type === 'configuration') return 'config';
  if (type === 'log') return 'log';
  if (type.includes('firmware') || type === 'fw_bundle') return 'firmware';

  const n = (f.name || '').toLowerCase();
  if (n.endsWith('.cfg')) return 'config';
  if (n.endsWith('.zip')) return 'log';
  if (n.endsWith('.xim') || n.endsWith('.fwc') || n.endsWith('.tpack')) return 'firmware';
  return 'other';
};

interface VehiclePreview {
  vehicle_id: number;
  registration_number: string;
  vehicle_name: string | null;
  imei: string;
}

interface SendResult extends VehiclePreview {
  task_id: string | null;
  success: boolean;
  error?: string;
}

type TaskStatus = 'queued' | 'downloading' | 'applied' | 'failed';

interface FotaTask {
  id: number;
  task_id: string;
  imei: string;
  vehicle_id: number | null;
  file_id: number;
  file_name: string | null;
  status: TaskStatus;
  status_detail: string | null;
  organization_id: number | null;
  created_at: string;
  updated_at: string;
  registration_number: string | null;
  vehicle_name: string | null;
}

// ─── Hjälpkomponent: Status-ikon ─────────────────────────────────────────────

const StatusIcon = ({ status }: { status: TaskStatus }) => {
  if (status === 'applied') return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
  if (status === 'failed') return <XCircleIcon className="w-4 h-4 text-red-500" />;
  if (status === 'downloading') return <ArrowDownTrayIcon className="w-4 h-4 text-blue-500 animate-pulse" />;
  return <ClockIcon className="w-4 h-4 text-amber-500" />;
};

const statusLabel = (s: TaskStatus, t: (k: string) => string): string => {
  if (s === 'applied') return t('fotaMigration.taskApplied');
  if (s === 'failed') return t('fotaMigration.taskFailed');
  if (s === 'downloading') return t('fotaMigration.taskDownloading');
  return t('fotaMigration.taskQueued');
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const FileKindBadge = ({ kind, t }: { kind: FileKind; t: (k: string) => string }) => {
  const style =
    kind === 'config'
      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
      : kind === 'firmware'
      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
      : kind === 'log'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  const label =
    kind === 'config'
      ? t('fotaMigration.kindConfig')
      : kind === 'firmware'
      ? t('fotaMigration.kindFirmware')
      : kind === 'log'
      ? t('fotaMigration.kindLog')
      : t('fotaMigration.kindOther');
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${style}`}>
      {label}
    </span>
  );
};

// ─── Huvudkomponent ───────────────────────────────────────────────────────────

const FotaWebMigration = () => {
  const { t } = useTranslation();

  // Organisation
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgSearch, setOrgSearch] = useState('');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  // FOTA-filer (alla typer: config, firmware, log)
  const [fotaFiles, setFotaFiles] = useState<FotaFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FotaFile | null>(null);
  const [showFileDropdown, setShowFileDropdown] = useState(false);
  const [fileFilter, setFileFilter] = useState<FileFilter>('all');
  const fileDropdownRef = useRef<HTMLDivElement>(null);

  // Uppladdning
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Förhandsvisning
  const [previewing, setPreviewing] = useState(false);
  const [previewVehicles, setPreviewVehicles] = useState<VehiclePreview[] | null>(null);

  // Sändning
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<SendResult[] | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sendMode, setSendMode] = useState<'all' | 'selected'>('all');
  const [selectedImeis, setSelectedImeis] = useState<Set<string>>(new Set());
  const [sendFileType, setSendFileType] = useState<'config' | 'firmware'>('config');

  // Statusspårning
  const [tasks, setTasks] = useState<FotaTask[] | null>(null);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState<'send' | 'status' | 'files'>('send');

  // Filhantering
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);

  // ─── Init ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    loadOrganizations();
    loadFotaFiles();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(e.target as Node)) {
        setShowOrgDropdown(false);
      }
      if (fileDropdownRef.current && !fileDropdownRef.current.contains(e.target as Node)) {
        setShowFileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Data-laddning ────────────────────────────────────────────────────────

  const loadOrganizations = async () => {
    try {
      const res = await api.get('/api/organizations/top-nodes');
      const data = res.data?.data || res.data || [];
      setOrganizations(Array.isArray(data) ? data : []);
    } catch {
      /* tysta fel */
    }
  };

  const loadFotaFiles = async () => {
    setFilesLoading(true);
    try {
      const res = await api.get('/api/fota/files');
      const all: FotaFile[] = res.data?.data || [];
      setFotaFiles(all);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } } };
      const serverMsg = ax?.response?.data?.error;
      toast.error(
        serverMsg ? `${t('fotaMigration.errorLoadingFiles')}: ${serverMsg}` : t('fotaMigration.errorLoadingFiles'),
      );
    } finally {
      setFilesLoading(false);
    }
  };

  const handleDeleteFile = async (file: FotaFile) => {
    setDeleteConfirmId(null);
    setDeletingFileId(file.id);
    try {
      await api.delete(`/api/fota/files/${file.id}`);
      toast.success(t('fotaMigration.fileDeleted').replace('{name}', file.name));
      if (selectedFile?.id === file.id) setSelectedFile(null);
      await loadFotaFiles();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('fotaMigration.fileDeleteError'));
    } finally {
      setDeletingFileId(null);
    }
  };

  const handleDownloadFile = async (file: FotaFile) => {
    setDownloadingFileId(file.id);
    try {
      const res = await api.get(`/api/fota/files/${file.id}/download?name=${encodeURIComponent(file.name)}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      const msg = err?.response?.data?.error || t('fotaMigration.fileDownloadError');
      toast.error(msg, { duration: 6000 });
    } finally {
      setDownloadingFileId(null);
    }
  };

  // ─── Organisation ─────────────────────────────────────────────────────────

  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(orgSearch.toLowerCase()),
  );

  const selectOrg = (org: Organization) => {
    setSelectedOrg(org);
    setOrgSearch(org.name);
    setShowOrgDropdown(false);
    setPreviewVehicles(null);
    setResults(null);
  };

  // ─── Uppladdning ──────────────────────────────────────────────────────────

  const uploadFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.cfg')) {
      toast.error(t('fotaMigration.onlyCfgAllowed'));
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/api/fota/upload-file', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const result = res.data?.data;
      toast.success(t('fotaMigration.uploadSuccess').replace('{name}', result?.fileName || file.name));
      await loadFotaFiles();
      // Välj automatiskt den nyuppladdade filen
      if (result?.fileId) {
        setSelectedFile({ id: result.fileId, name: result.fileName, size: file.size });
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('fotaMigration.uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }, []);

  // ─── Förhandsgranskning ───────────────────────────────────────────────────

  const handlePreview = async () => {
    if (!selectedOrg) { toast.error(t('fotaMigration.selectOrgFirst')); return; }
    setPreviewing(true);
    setPreviewVehicles(null);
    setResults(null);
    try {
      const res = await api.post('/api/fota/preview-organization', { organizationId: selectedOrg.id });
      const vehicles: VehiclePreview[] = res.data?.data?.vehicles || [];
      setPreviewVehicles(vehicles);
      // Förvälj alla fordon så att "Valda"-läget startar med allt ikryssat
      setSelectedImeis(new Set(vehicles.map(v => v.imei)));
    } catch {
      toast.error(t('fotaMigration.errorLoading'));
    } finally {
      setPreviewing(false);
    }
  };

  // ─── Sändning ─────────────────────────────────────────────────────────────

  const handleSendConfirmed = async () => {
    setShowConfirm(false);
    setSending(true);
    setResults(null);
    try {
      const res = await api.post('/api/fota/send-config', {
        organizationId: selectedOrg!.id,
        fileId: selectedFile!.id,
        fileName: selectedFile!.name,
        taskType: sendFileType === 'firmware' ? 'TxFirmware' : 'TxConfiguration',
        ...(sendMode === 'selected'
          ? { imeis: (previewVehicles ?? []).filter(v => selectedImeis.has(v.imei)).map(v => v.imei) }
          : {}),
      });
      const data = res.data?.data;
      setResults(data?.results || []);
      const succeeded = data?.succeeded ?? 0;
      const total = data?.totalSent ?? 0;
      const msg = t('fotaMigration.successMessage')
        .replace('{succeeded}', String(succeeded))
        .replace('{total}', String(total));
      succeeded === total ? toast.success(msg) : toast.error(msg);
      // Ladda om status-tabben
      if (selectedOrg) refreshTaskStatus(selectedOrg.id);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('fotaMigration.errorSending'));
    } finally {
      setSending(false);
    }
  };

  // ─── Statusspårning ───────────────────────────────────────────────────────

  const refreshTaskStatus = async (orgId?: number) => {
    const id = orgId ?? selectedOrg?.id;
    if (!id) return;
    setRefreshingStatus(true);
    try {
      const res = await api.get(`/api/fota/tasks-status?organizationId=${id}`);
      setTasks(res.data?.data || []);
    } catch {
      toast.error(t('fotaMigration.errorLoadingStatus'));
    } finally {
      setRefreshingStatus(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const vehicleCount = previewVehicles?.length ?? 0;
  const selectedCount = (previewVehicles ?? []).filter(v => selectedImeis.has(v.imei)).length;
  const effectiveCount = sendMode === 'selected' ? selectedCount : vehicleCount;
  const canSend =
    !!selectedOrg && !!selectedFile && !!previewVehicles && previewVehicles.length > 0 && effectiveCount > 0;

  const toggleVehicle = (imei: string) => {
    setSelectedImeis(prev => {
      const next = new Set(prev);
      if (next.has(imei)) next.delete(imei);
      else next.add(imei);
      return next;
    });
  };

  const toggleAllVehicles = () => {
    const all = previewVehicles ?? [];
    setSelectedImeis(prev => (prev.size === all.length ? new Set() : new Set(all.map(v => v.imei))));
  };

  // Filer per typ. Config (.cfg) skickas via TxConfiguration, firmware (.xim) via TxFirmware.
  const configFiles = fotaFiles.filter(f => getFileKind(f) === 'config');
  const firmwareFiles = fotaFiles.filter(f => getFileKind(f) === 'firmware');
  const sendableFiles = sendFileType === 'firmware' ? firmwareFiles : configFiles;

  // Antal per typ + lista filtrerad efter vald flik i Filer-tabben
  const kindCounts = {
    all: fotaFiles.length,
    config: fotaFiles.filter(f => getFileKind(f) === 'config').length,
    firmware: fotaFiles.filter(f => getFileKind(f) === 'firmware').length,
    log: fotaFiles.filter(f => getFileKind(f) === 'log').length,
  };
  const filteredFiles =
    fileFilter === 'all' ? fotaFiles : fotaFiles.filter(f => getFileKind(f) === fileFilter);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <ArrowPathIcon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('fotaMigration.title')}
          </h1>
        </div>
        <p className="text-sm text-textTertiary dark:text-textTertiary">{t('fotaMigration.description')}</p>
        <div className="mt-3 flex items-start gap-2 text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/40 rounded-lg px-3 py-2">
          <InformationCircleIcon className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{t('fotaMigration.wakeupInfo')}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        {(['send', 'status', 'files'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              if (tab === 'status' && selectedOrg) refreshTaskStatus();
            }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              activeTab === tab
                ? 'bg-white dark:bg-gray-800 border border-b-white dark:border-gray-700 dark:border-b-gray-800 text-blue-600 dark:text-blue-400 -mb-px'
                : 'text-textTertiary dark:text-textTertiary hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'send'
              ? t('fotaMigration.tabSend')
              : tab === 'status'
              ? t('fotaMigration.tabStatus')
              : t('fotaMigration.tabFiles')}
          </button>
        ))}
      </div>

      {/* ── Tab: Skicka ── */}
      {activeTab === 'send' && (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <div className="grid grid-cols-1 gap-6">

              {/* Typ av leverans: config eller firmware */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fotaMigration.deliveryType')}
                </label>
                <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
                  {(['config', 'firmware'] as const).map(type => (
                    <button
                      key={type}
                      onClick={() => {
                        setSendFileType(type);
                        setSelectedFile(null);
                      }}
                      className={`px-4 py-2 font-medium transition-colors ${
                        sendFileType === type
                          ? 'bg-blue-600 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {type === 'config' ? t('fotaMigration.kindConfig') : t('fotaMigration.kindFirmware')}
                    </button>
                  ))}
                </div>
                {sendFileType === 'firmware' && (
                  <div className="mt-2 flex items-start gap-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg px-3 py-2">
                    <ExclamationTriangleIcon className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{t('fotaMigration.firmwareWarning')}</span>
                  </div>
                )}
              </div>

              {/* Uppladdning (endast config — firmware tillhandahålls av Teltonika) */}
              {sendFileType === 'config' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('fotaMigration.uploadFile')}
                </label>
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative cursor-pointer border-2 border-dashed rounded-lg px-6 py-8 text-center transition-colors ${
                    dragOver
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".cfg"
                    className="hidden"
                    onChange={handleFileInputChange}
                  />
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <ArrowPathIcon className="w-8 h-8 text-blue-500 animate-spin" />
                      <span className="text-sm text-textTertiary dark:text-textTertiary">{t('fotaMigration.uploading')}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <CloudArrowUpIcon className="w-8 h-8 text-textTertiary dark:text-textTertiary" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('fotaMigration.dropCfgHere')}
                      </span>
                      <span className="text-xs text-textTertiary">{t('fotaMigration.onlyCfgAllowed')}</span>
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* Filval (config eller firmware) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {sendFileType === 'firmware'
                    ? t('fotaMigration.selectFirmwareFile')
                    : t('fotaMigration.selectConfigFile')}
                </label>
                <div className="relative" ref={fileDropdownRef}>
                  <div
                    className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 cursor-pointer"
                    onClick={() => setShowFileDropdown(v => !v)}
                  >
                    <span className="flex-1 px-3 py-2.5 text-sm text-gray-900 dark:text-white truncate">
                      {selectedFile
                        ? `${selectedFile.name} (${formatBytes(selectedFile.size)})`
                        : sendFileType === 'firmware'
                        ? t('fotaMigration.firmwareFilePlaceholder')
                        : t('fotaMigration.configFilePlaceholder')}
                    </span>
                    <ChevronDownIcon className="w-4 h-4 mr-3 text-textTertiary shrink-0" />
                  </div>
                  {showFileDropdown && (
                    <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
                      {filesLoading ? (
                        <div className="px-4 py-3 text-sm text-textTertiary">{t('common.loading')}</div>
                      ) : sendableFiles.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-textTertiary">{t('fotaMigration.noFilesFound')}</div>
                      ) : (
                        sendableFiles.map(f => (
                          <button
                            key={f.id}
                            onClick={() => { setSelectedFile(f); setShowFileDropdown(false); }}
                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-50 dark:hover:bg-gray-700"
                          >
                            <span className="font-medium text-gray-900 dark:text-white">{f.name}</span>
                            <span className="ml-2 text-xs text-textTertiary">{formatBytes(f.size)}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Organisation-väljare */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fotaMigration.selectOrganization')}
                </label>
                <div className="relative" ref={orgDropdownRef}>
                  <div
                    className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 cursor-pointer"
                    onClick={() => setShowOrgDropdown(v => !v)}
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 ml-3 text-textTertiary shrink-0" />
                    <input
                      type="text"
                      value={orgSearch}
                      onChange={e => {
                        setOrgSearch(e.target.value);
                        setShowOrgDropdown(true);
                        if (!e.target.value) setSelectedOrg(null);
                      }}
                      placeholder={t('fotaMigration.organizationPlaceholder')}
                      className="flex-1 px-3 py-2.5 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
                    />
                    <ChevronDownIcon className="w-4 h-4 mr-3 text-textTertiary shrink-0" />
                  </div>
                  {showOrgDropdown && filteredOrgs.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg">
                      {filteredOrgs.map(org => (
                        <button
                          key={org.id}
                          onClick={() => selectOrg(org)}
                          className="w-full text-left px-4 py-2.5 text-sm text-gray-900 dark:text-white hover:bg-blue-50 dark:hover:bg-gray-700"
                        >
                          {org.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Knappar */}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handlePreview}
                  disabled={previewing || !selectedOrg}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <EyeIcon className="w-4 h-4" />
                  {previewing ? t('fotaMigration.previewing') : t('fotaMigration.previewBtn')}
                </button>

                <button
                  onClick={() => {
                    if (!selectedOrg) { toast.error(t('fotaMigration.selectOrgFirst')); return; }
                    if (!selectedFile) { toast.error(t('fotaMigration.selectFilFirst')); return; }
                    if (!previewVehicles || previewVehicles.length === 0) { toast.error(t('fotaMigration.noVehicles')); return; }
                    if (sendMode === 'selected' && selectedCount === 0) { toast.error(t('fotaMigration.noVehiclesSelected')); return; }
                    setShowConfirm(true);
                  }}
                  disabled={sending || !canSend}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  {sending ? t('fotaMigration.sending') : t('fotaMigration.sendBtn')}
                </button>
              </div>
            </div>
          </div>

          {/* Förhandsgranskning */}
          {previewVehicles !== null && !results && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {sendMode === 'selected'
                    ? `${selectedCount} / ${previewVehicles.length} ${t('fotaMigration.vehiclesSelected')}`
                    : `${previewVehicles.length} ${t('fotaMigration.vehiclesFound')}`}
                </h2>
                {previewVehicles.length > 0 && (
                  <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-sm">
                    {(['all', 'selected'] as const).map(mode => (
                      <button
                        key={mode}
                        onClick={() => setSendMode(mode)}
                        className={`px-3 py-1.5 font-medium transition-colors ${
                          sendMode === mode
                            ? 'bg-blue-600 text-white'
                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {mode === 'all' ? t('fotaMigration.sendToAll') : t('fotaMigration.sendToSelected')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {previewVehicles.length === 0 ? (
                <p className="text-sm text-textTertiary dark:text-textTertiary">{t('fotaMigration.noVehicles')}</p>
              ) : (
                <VehicleTable
                  vehicles={previewVehicles}
                  t={t}
                  selectable={sendMode === 'selected'}
                  selectedImeis={selectedImeis}
                  onToggle={toggleVehicle}
                  onToggleAll={toggleAllVehicles}
                />
              )}
            </div>
          )}

          {/* Sändningsresultat */}
          {results !== null && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
              <div className="flex items-center gap-6 mb-4">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  {t('fotaMigration.resultTitle')}
                </h2>
                <div className="flex gap-4 text-sm">
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="w-4 h-4" />
                    {t('fotaMigration.succeeded')}: {results.filter(r => r.success).length}
                  </span>
                  <span className="flex items-center gap-1 text-red-500 dark:text-red-400">
                    <XCircleIcon className="w-4 h-4" />
                    {t('fotaMigration.failed')}: {results.filter(r => !r.success).length}
                  </span>
                </div>
              </div>
              <SendResultTable results={results} t={t} />
            </div>
          )}
        </>
      )}

      {/* ── Tab: Status ── */}
      {activeTab === 'status' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('fotaMigration.taskStatusTitle')}
            </h2>
            <button
              onClick={() => refreshTaskStatus()}
              disabled={refreshingStatus || !selectedOrg}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className={`w-4 h-4 ${refreshingStatus ? 'animate-spin' : ''}`} />
              {t('fotaMigration.refreshStatus')}
            </button>
          </div>

          {!selectedOrg ? (
            <p className="text-sm text-textTertiary">{t('fotaMigration.selectOrgForStatus')}</p>
          ) : tasks === null ? (
            <p className="text-sm text-textTertiary">{t('fotaMigration.clickRefreshToLoad')}</p>
          ) : tasks.length === 0 ? (
            <p className="text-sm text-textTertiary">{t('fotaMigration.noTasksFound')}</p>
          ) : (
            <TaskStatusTable tasks={tasks} t={t} />
          )}
        </div>
      )}

      {/* ── Tab: Filer ── */}
      {activeTab === 'files' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {t('fotaMigration.fileListTitle')}
            </h2>
            <button
              onClick={loadFotaFiles}
              disabled={filesLoading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 ${filesLoading ? 'animate-spin' : ''}`} />
              {t('fotaMigration.refreshStatus')}
            </button>
          </div>

          {/* Upload-yta även på filfliken */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`mb-6 cursor-pointer border-2 border-dashed rounded-lg px-6 py-6 text-center transition-colors ${
              dragOver
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
            }`}
          >
            {uploading ? (
              <div className="flex items-center justify-center gap-2">
                <ArrowPathIcon className="w-5 h-5 text-blue-500 animate-spin" />
                <span className="text-sm text-textTertiary">{t('fotaMigration.uploading')}</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <CloudArrowUpIcon className="w-5 h-5 text-textTertiary" />
                <span className="text-sm text-textSecondary dark:text-gray-300">{t('fotaMigration.dropCfgHere')}</span>
              </div>
            )}
          </div>

          {/* Typfilter */}
          <div className="flex flex-wrap gap-2 mb-4">
            {(['all', 'config', 'firmware', 'log'] as const).map(filter => {
              const label =
                filter === 'all'
                  ? t('fotaMigration.filterAll')
                  : filter === 'config'
                  ? t('fotaMigration.kindConfig')
                  : filter === 'firmware'
                  ? t('fotaMigration.kindFirmware')
                  : t('fotaMigration.kindLog');
              return (
                <button
                  key={filter}
                  onClick={() => setFileFilter(filter)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    fileFilter === filter
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  {label} <span className="opacity-70">({kindCounts[filter]})</span>
                </button>
              );
            })}
          </div>

          {/* Fillista */}
          {filesLoading ? (
            <p className="text-sm text-textTertiary">{t('common.loading')}</p>
          ) : filteredFiles.length === 0 ? (
            <p className="text-sm text-textTertiary">{t('fotaMigration.noFilesFound')}</p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredFiles.map(f => (
                <div key={f.id} className="flex items-center gap-3 py-3">
                  <DocumentIcon className="w-5 h-5 text-textTertiary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{f.name}</p>
                      <FileKindBadge kind={getFileKind(f)} t={t} />
                    </div>
                    <p className="text-xs text-textTertiary">
                      {formatBytes(f.size)}
                      {f.created_at && (
                        <> &middot; {new Intl.DateTimeFormat('sv-SE', { dateStyle: 'short' }).format(new Date(f.created_at))}</>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {deleteConfirmId === f.id ? (
                      <>
                        <span className="text-xs text-red-500 mr-1">{t('fotaMigration.confirmDelete')}</span>
                        <button
                          onClick={() => handleDeleteFile(f)}
                          className="px-2 py-1 text-xs font-medium bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          {t('common.delete')}
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="px-2 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300"
                        >
                          {t('common.cancel')}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleDownloadFile(f)}
                          disabled={downloadingFileId === f.id}
                          className="p-1.5 rounded-lg text-textTertiary hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
                          title={t('fotaMigration.downloadFile')}
                        >
                          {downloadingFileId === f.id
                            ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            : <ArrowDownTrayIcon className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(f.id)}
                          disabled={deletingFileId === f.id}
                          className="p-1.5 rounded-lg text-textTertiary hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                          title={t('fotaMigration.deleteFile')}
                        >
                          {deletingFileId === f.id
                            ? <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            : <TrashIcon className="w-4 h-4" />}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bekräftelsedialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-500" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                {t('fotaMigration.confirmTitle')}
              </h3>
            </div>
            <p className="text-sm text-textSecondary dark:text-gray-300 mb-2">
              {t('fotaMigration.confirmMessage')
                .replace('{count}', String(effectiveCount))
                .replace('{file}', selectedFile?.name || '')}
            </p>
            <p className="text-xs text-textTertiary mb-6">{t('fotaMigration.confirmWakeupNote')}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSendConfirmed}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Delkomponenter ───────────────────────────────────────────────────────────

const VehicleTable = ({
  vehicles,
  t,
  selectable = false,
  selectedImeis,
  onToggle,
  onToggleAll,
}: {
  vehicles: VehiclePreview[];
  t: (k: string) => string;
  selectable?: boolean;
  selectedImeis?: Set<string>;
  onToggle?: (imei: string) => void;
  onToggleAll?: () => void;
}) => {
  const allSelected = selectable && !!selectedImeis && vehicles.length > 0 && vehicles.every(v => selectedImeis.has(v.imei));
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            {selectable && (
              <th className="text-left py-2 px-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleAll}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                  aria-label={t('fotaMigration.selectAll')}
                />
              </th>
            )}
            <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.registrationNumber')}</th>
            <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.vehicleName')}</th>
            <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.imei')}</th>
          </tr>
        </thead>
        <tbody>
          {vehicles.map(v => {
            const checked = !!selectedImeis?.has(v.imei);
            return (
              <tr
                key={v.vehicle_id}
                onClick={selectable ? () => onToggle?.(v.imei) : undefined}
                className={`border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
                  selectable ? 'cursor-pointer' : ''
                } ${selectable && checked ? 'bg-blue-50/60 dark:bg-blue-900/10' : ''}`}
              >
                {selectable && (
                  <td className="py-2 px-3" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => onToggle?.(v.imei)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      aria-label={v.registration_number || v.imei}
                    />
                  </td>
                )}
                <td className="py-2 px-3 font-mono font-medium text-gray-900 dark:text-white">{v.registration_number || '—'}</td>
                <td className="py-2 px-3 text-textSecondary dark:text-gray-300">{v.vehicle_name || '—'}</td>
                <td className="py-2 px-3 font-mono text-xs text-textTertiary dark:text-textTertiary">{v.imei}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SendResultTable = ({ results, t }: { results: SendResult[]; t: (k: string) => string }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.registrationNumber')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.imei')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.status')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.taskId')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.errorMsg')}</th>
        </tr>
      </thead>
      <tbody>
        {results.map(r => (
          <tr key={r.imei} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
            <td className="py-2 px-3 font-mono font-medium text-gray-900 dark:text-white">{r.registration_number || '—'}</td>
            <td className="py-2 px-3 font-mono text-xs text-textTertiary dark:text-textTertiary">{r.imei}</td>
            <td className="py-2 px-3">
              {r.success ? (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <ClockIcon className="w-4 h-4" />
                  {t('fotaMigration.taskQueued')}
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-500">
                  <XCircleIcon className="w-4 h-4" />
                  {t('fotaMigration.columnFailed')}
                </span>
              )}
            </td>
            <td className="py-2 px-3 font-mono text-xs text-textTertiary">{r.task_id || '—'}</td>
            <td className="py-2 px-3 text-xs text-red-400">{r.error || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const TaskStatusTable = ({ tasks, t }: { tasks: FotaTask[]; t: (k: string) => string }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.registrationNumber')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.imei')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.configFile')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.status')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.taskId')}</th>
          <th className="text-left py-2 px-3 font-medium text-textSecondary dark:text-textTertiary">{t('fotaMigration.lastUpdated')}</th>
        </tr>
      </thead>
      <tbody>
        {tasks.map(task => (
          <tr key={task.id} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30">
            <td className="py-2 px-3 font-mono font-medium text-gray-900 dark:text-white">{task.registration_number || '—'}</td>
            <td className="py-2 px-3 font-mono text-xs text-textTertiary dark:text-textTertiary">{task.imei}</td>
            <td className="py-2 px-3 text-xs text-textTertiary dark:text-textTertiary max-w-[140px] truncate" title={task.file_name || ''}>
              {task.file_name || '—'}
            </td>
            <td className="py-2 px-3">
              <span className="flex items-center gap-1.5">
                <StatusIcon status={task.status} />
                <span className={
                  task.status === 'applied' ? 'text-green-600 dark:text-green-400' :
                  task.status === 'failed' ? 'text-red-500' :
                  task.status === 'downloading' ? 'text-blue-500' :
                  'text-amber-500'
                }>
                  {statusLabel(task.status, t)}
                </span>
              </span>
            </td>
            <td className="py-2 px-3 font-mono text-xs text-textTertiary">{task.task_id}</td>
            <td className="py-2 px-3 text-xs text-textTertiary">
              {new Intl.DateTimeFormat('sv-SE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(task.updated_at))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default FotaWebMigration;
